import {Injectable, OnDestroy, OnInit} from '@angular/core';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {AppContainerService, PersonalizationService, SideNavBarService} from '@mydocma/app-container';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, combineLatest, firstValueFrom, map} from 'rxjs';
import {MmPlanEditService} from '../../mm-plans-view/mm-plan-view/services/mm-plan-edit.service';
import {MmPlansManagerService} from '../../mm-plans-view/services/mm-plans-manager.service';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {MmViewService} from '../../../mm-view.service';
import {ActivatedRoute} from '@angular/router';

export enum HeaderToggleMenuEnum {
  gridView = "gridView",
  tileView = 'tileView',
  planView = 'planView'
}

@UntilDestroy()
@Injectable()
export class MmFaultsSplitService implements OnInit, OnDestroy {

  private toggledLeftViewsSubject = new BehaviorSubject<any[]>([]);
  toggledLeftViews$ = this.toggledLeftViewsSubject.asObservable();

  previousToggleValue: string;
  currentToggleValue: string;
  toggledLeftViews = [];
  toggledRightViews = [];
  leftAreaSize: any = 50;
  rightAreaSize: any = 50;
  layoutSettingsInitialized = false;


  // after plan closed saved value will be set ot toggle group
  public lastToggleViewBeforePlanSelected: HeaderToggleMenuEnum;
  private rightViewVisibleSubject = new BehaviorSubject<boolean>(false);

  //used in mm fault header comp to check last toggle state before plan view selected in tablet
  rightViewVisible$ = this.rightViewVisibleSubject.asObservable();

  constructor(
              private readonly personalizationService: PersonalizationService,
              private readonly plansManagerService: MmPlansManagerService,
              private readonly planEditSerivce: MmPlanEditService,
              private sideNavBarService: SideNavBarService,
              private mmViewService: MmViewService,
              public deviceService: DeviceService,
              private readonly activatedRoute: ActivatedRoute,
              private readonly appContainerService: AppContainerService) {

    this.planEditSerivce.attachTo(this);
    if (this.appContainerService.isMobileView) {
      this.setLeftButtonViewValue([])
      this.toggledLeftViews = [HeaderToggleMenuEnum.tileView];
      this.toggledLeftViewsSubject.next(this.toggledLeftViews);
    }
  }

  get rightViewVisible(): boolean {
    return this.rightViewVisibleSubject.value;
  }


  ngOnInit() {

  }

  ngOnDestroy(): void {
  }

  public toggleView(value: HeaderToggleMenuEnum): void {


    if (value === HeaderToggleMenuEnum.planView) {
      this.togglePlanView()
    } else {
      this.toggledLeftViews = [value];
      this.toggledLeftViewsSubject.next(this.toggledLeftViews);
      if (this.deviceService.isTablet()) {
        this.setRightViewVisble(false)
      }
    }
    this.manageToggleState(value);

    this.saveLayoutSettings();
  }


  public saveAreaSize({gutterNum, sizes}): void {
    this.leftAreaSize = sizes[0];
    this.rightAreaSize = sizes[1];
    this.saveLayoutSettings();
  }

  private manageToggleState(value): void {
    if (value !== this.currentToggleValue) {
      this.previousToggleValue = this.currentToggleValue;
      this.currentToggleValue = value;
    }
  }

  private async saveLayoutSettings() {
    await this.personalizationService.setValue(PersonalizationKeysEnum.Fault_PlanSplit_LeftViewSize, this.leftAreaSize);
    await this.personalizationService.setValue(PersonalizationKeysEnum.Fault_PlanSplit_RightViewSize, this.rightAreaSize);
    await this.personalizationService.setValue(PersonalizationKeysEnum.Fault_LeftToggleGroup, this.toggledLeftViews);
    await this.personalizationService.setValue(PersonalizationKeysEnum.Fault_RightToggleGroup, this.toggledRightViews);
  }

  private async loadLayoutSettings() {
    await firstValueFrom(combineLatest([
      this.personalizationService.getValue(PersonalizationKeysEnum.Fault_PlanSplit_LeftViewSize),
      this.personalizationService.getValue(PersonalizationKeysEnum.Fault_PlanSplit_RightViewSize),
      this.personalizationService.getValue(PersonalizationKeysEnum.Fault_LeftToggleGroup),
      this.personalizationService.getValue(PersonalizationKeysEnum.Fault_RightToggleGroup)
    ]).pipe(untilDestroyed(this),
      map(([leftAreaSizeLoaded, rightAreaSizeLoaded, toggledLeftViewsLoaded, toggledRightViewsLoaded]) => {

        this.setLeftButtonViewValue(toggledLeftViewsLoaded);
        this.setRightButtonViewValue(toggledRightViewsLoaded)

        this.leftAreaSize = (leftAreaSizeLoaded ? leftAreaSizeLoaded : this.leftAreaSize);
        this.rightAreaSize = (rightAreaSizeLoaded ? rightAreaSizeLoaded : this.rightAreaSize);
        this.setRightViewVisibility();
      }))).catch((e) => {
      console.error(`an error ${e} occurred while loading layout configurations`)
    });
  }

  public async disablePlanView() {
    if (!this.appContainerService.isMobileView) {
      if (!this.layoutSettingsInitialized) {
        this.layoutSettingsInitialized = true;
        await this.loadLayoutSettings();
      }

      this.toggledRightViews = [];
      await this.setRightViewVisble(false);
      this.saveLayoutSettings();
    }
  }

  public async setRightViewVisble(visible: boolean) {
    if (visible) {
      this.toggledRightViews = [HeaderToggleMenuEnum.planView];
      if (this.deviceService.isTablet()) {
        this.toggledLeftViews = [];
        this.toggledLeftViewsSubject.next(this.toggledLeftViews);
        this.mmViewService.closeFilter();
      } else if (this.appContainerService.isMobileView) {
        this.toggledLeftViews = [];
        this.toggledLeftViewsSubject.next(this.toggledLeftViews);
      }
    } else {
      this.toggledRightViews = [];
      if (this.appContainerService.isMobileView) {
        this.toggledLeftViews = [HeaderToggleMenuEnum.tileView];
        this.toggledLeftViewsSubject.next(this.toggledLeftViews);
      }

      if (!this.toggledLeftViews?.length) {
        this.toggledLeftViews = [this.previousToggleValue];
        this.toggledLeftViewsSubject.next(this.toggledLeftViews);
      }

      await this.plansManagerService.setSelectedPlan(null);
    }

    this.rightViewVisibleSubject.next(visible);
  }

  private setRightButtonViewValue(toggledRightViewsLoaded: string[]) {
    const isTablet = this.deviceService.isTablet();
    if (isTablet && this.toggledLeftViews.length) {
      return
    }
    this.toggledRightViews = toggledRightViewsLoaded;
  }

  private setLeftButtonViewValue(toggledLeftViewsLoaded: string[]) {
    // create
    const isMobile = this.appContainerService.isMobileView;
    const defaultValue = isMobile ? [HeaderToggleMenuEnum.tileView] : [HeaderToggleMenuEnum.gridView];
    const isLeftViewContainsInitialValue = toggledLeftViewsLoaded?.some(x => x === HeaderToggleMenuEnum.gridView || x === HeaderToggleMenuEnum.tileView)
    if (!isLeftViewContainsInitialValue || isMobile) {
      this.toggledLeftViews = defaultValue;
      this.toggledLeftViewsSubject.next(this.toggledLeftViews);
    } else {
      this.toggledLeftViews = toggledLeftViewsLoaded;
      this.toggledLeftViewsSubject.next(this.toggledLeftViews);
    }

    this.previousToggleValue = this.toggledLeftViews[0];
    this.currentToggleValue = this.toggledLeftViews[0];
  }

  private setRightViewVisibility() {
    if (!this.toggledRightViews?.length) {
      this.setRightViewVisble(false);
    } else {
      this.setRightViewVisble(true);
    }
  }


  // }

  // open or close right panel with plan
  // changes state of header toggle buttons depending on device type
  private async togglePlanView(skipPlanSelection = false) {
    if (this.toggledLeftViews.length === 0) {
      this.toggledLeftViews.push(HeaderToggleMenuEnum.gridView);
      this.toggledLeftViewsSubject.next(this.toggledLeftViews);
    }
    if (this.toggledRightViews.length > 0 && !this.deviceService.isTablet()) {
      await this.setRightViewVisble(false);
    } else {
      this.plansManagerService.setPlanFlyoutVisible(false);
      await this.setRightViewVisble(true);
      if (this.plansManagerService.plansByStructure.length > 0 && !skipPlanSelection) {
        this.plansManagerService.setSelectedPlan(this.plansManagerService.plansByStructure[0]);
      }
    }
  }

}
